<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">

    <!-- Sidebar -->
    <HrSidebar :isOpen="isSidebarOpen" />

    <!-- Main Content -->
    <main 
      class="flex-1 overflow-x-hidden transition-all duration-300"
      :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
    >

      <!-- Navbar -->
      <header class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm">
        <HrTopbar
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- Content Area -->
      <div class="p-6 mt-16">

        <!-- Top Bar -->
        <div class="flex justify-between items-center mb-8">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search employee actions..."
            class="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 w-72 focus:outline-none focus:ring focus:ring-blue-300 dark:bg-gray-700 dark:text-white"
          />

          <button
            @click="openModal()"
            class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f] shadow"
          >
            + Add Action
          </button>
        </div>

        <!-- Employee Actions Grid -->
        <div v-if="filteredActions.length" class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <div
            v-for="action in filteredActions"
            :key="action.id"
            class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-xl transition"
          >
            <div class="flex items-center space-x-3 mb-4">
              <div :class="['w-12 h-12 rounded-full flex items-center justify-center font-bold text-white', action.color]">
                {{ action.code }}
              </div>
              <div>
                <h3 class="font-semibold text-gray-800 dark:text-white">{{ action.name }}</h3>
                <span class="text-xs text-gray-500 dark:text-gray-400">Employee Action</span>
              </div>
            </div>

            <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">{{ action.description }}</p>

            <div class="flex justify-between text-sm">
              <button @click="editAction(action)" class="text-blue-600 hover:underline">Edit</button>
              <button @click="deleteAction(action.id)" class="text-red-500 hover:underline">Delete</button>
            </div>
          </div>
        </div>

        <div v-else class="text-center text-gray-400 mt-20">
          No employee actions created yet
        </div>

        <!-- Modal -->
        <Transition name="modal">
          <div v-if="showModal" class="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px] shadow-lg">
              <h3 class="text-lg font-semibold text-[#035a76] mb-6">
                {{ editing ? 'Edit Employee Action' : 'Add Employee Action' }}
              </h3>

              <form @submit.prevent="saveAction" class="space-y-4">
                <input
                  v-model="form.name"
                  type="text"
                  placeholder="Action Name"
                  required
                  class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
                />

                <input
                  v-model="form.code"
                  type="text"
                  placeholder="Code (PRM, DEM, TRF...)"
                  required
                  class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
                />

                <select
                  v-model="form.color"
                  class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
                >
                  <option value="bg-green-500">Green (Promotion)</option>
                  <option value="bg-red-500">Red (Termination/Demotion)</option>
                  <option value="bg-yellow-500">Yellow (Transfer/Warning)</option>
                  <option value="bg-blue-500">Blue (Other)</option>
                </select>

                <textarea
                  v-model="form.description"
                  placeholder="Description"
                  class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
                ></textarea>

                <div class="flex justify-end space-x-3 pt-2">
                  <button type="button" @click="closeModal" class="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                  <button type="submit" class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]">Save</button>
                </div>
              </form>
            </div>
          </div>
        </Transition>

      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import HrSidebar from '@/components/hr/HrSidebar.vue'
import HrTopbar from '@/components/hr/HrTopbar.vue'

const currentPageTitle = ref('Employee Actions')
const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const showModal = ref(false)
const editing = ref(false)

const searchQuery = ref('')

const actions = ref([
  { id: 1, name: 'Promotion', code: 'PRM', color: 'bg-green-500', description: 'Employee has been promoted' },
  { id: 2, name: 'Demotion', code: 'DEM', color: 'bg-red-500', description: 'Employee has been demoted' },
  { id: 3, name: 'Termination', code: 'TRM', color: 'bg-red-500', description: 'Employee contract ended' },
  { id: 4, name: 'Transfer', code: 'TRF', color: 'bg-yellow-500', description: 'Employee has been transferred' },
])

const form = ref({ id: null, name: '', code: '', color: 'bg-green-500', description: '' })

const filteredActions = computed(() =>
  actions.value.filter(a => a.name.toLowerCase().includes(searchQuery.value.toLowerCase()))
)

const openModal = () => {
  editing.value = false
  form.value = { id: null, name: '', code: '', color: 'bg-green-500', description: '' }
  showModal.value = true
}

const editAction = (action) => {
  editing.value = true
  form.value = { ...action }
  showModal.value = true
}

const saveAction = () => {
  if (editing.value) {
    const index = actions.value.findIndex(a => a.id === form.value.id)
    actions.value[index] = { ...form.value }
  } else {
    actions.value.push({ id: Date.now(), ...form.value })
  }
  closeModal()
}

const deleteAction = (id) => {
  actions.value = actions.value.filter(a => a.id !== id)
}

const closeModal = () => {
  showModal.value = false
}

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  if (isDarkMode.value) document.documentElement.classList.add('dark')
  else document.documentElement.classList.remove('dark')
}
</script>

<style>
.modal-enter-active,
.modal-leave-active {
  transition: all 0.25s ease;
}
.modal-enter-from {
  opacity: 0;
  transform: scale(0.9);
}
.modal-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
</style>